/**
 *
 * @module drmProvider
 * @see https://github.bamtech.co/sdk-doc/spec-sdk/blob/master/specs/feature_overviews/drm.md
 *
 */

import Check from 'check-types';
import EventEmitter from 'events';
import Logging from './../logging/entry';
import Services from './../services/entry';
import Token from './../token/entry';
import DrmManager from './drmManager';

const TokenManager = Token.TokenManager;
const DrmType = Services.Media.DrmType;

const InvalidArgumentException = Services.Exception.InvalidArgumentException;

const {
    instanceStrict,
    nonEmptyObject
} = Check.assert;

/**
 *
 * @class DrmProvider
 * @extends {EventEmitter}
 * @see https://nodejs.org/api/events.html
 * @since 3.2.0
 *
 */
export default class DrmProvider extends EventEmitter {

    /**
     *
     * @param {SDK.Drm.DrmManager} provider
     * @param {SDK.Token.TokenManager} tokenManager
     * @param {SDK.Logging.Logger} [logger=Logging]
     *
     */
    constructor(provider, tokenManager, logger = Logging) {

        const errorMessage = {
            ctor: 'DrmProvider(provider, tokenManager, logger)',
            get provider() {
                return `${this.ctor} needs valid provider SDK.Drm.DrmManager instance`;
            },
            get tokenManager() {
                return `${this.ctor} needs SDK.Token.TokenManager instance`;
            }
        };

        instanceStrict(provider, DrmManager, errorMessage.provider, InvalidArgumentException);
        instanceStrict(tokenManager, TokenManager, errorMessage.tokenManager, InvalidArgumentException);

        super();

        /**
         *
         * @access private
         * @type {SDK.Drm.DrmManager}
         *
         */
        this.provider = provider;

        /**
         *
         * @access private
         * @type {SDK.Token.TokenManager}
         *
         */
        this.tokenManager = tokenManager;

        /**
         *
         * @access private
         * @type {SDK.Logging.Logger}
         *
         */
        this.logger = logger;

        /**
         *
         * @access private
         * @type {String}
         * @desc DRM identifier String
         *
         */
        this.type = DrmType.NONE;

        /**
         *
         * @access private
         * @type {Object}
         * @desc Contains license request headers if any.
         *
         */
        this.licenseRequestHeaders = {};

        /**
         *
         * @access private
         * @type {Object}
         * @desc Contains certificate headers if any.
         *
         */
        this.certificateHeaders = {};

        /**
         *
         * @access private
         * @type {String}
         * @desc Media failure identifier String
         *
         */
        this.failureEvent = 'MediaFailure';

        this.logger.log(DrmProvider.name, 'Created.');

    }

    /* #region private: */

    /**
     *
     * @access private
     * @desc Grabs a fresh AccessToken from the AccessTokenProvider instance
     * @returns {SDK.Token.AccessToken}
     *
     */
    get accessToken() {

        return this.tokenManager.getAccessToken();

    }

    /**
     *
     * @access private
     * @desc Returns an object containing a modified copy of license headers if they exist
     * @returns {Object}
     *
     */
    processLicenseRequestHeaders() {

        const headersCopy = Object.assign({}, this.licenseRequestHeaders);
        const authHeader = headersCopy.Authorization;

        if (authHeader) {
            headersCopy.Authorization = authHeader.replace('{accessToken}', this.accessToken.token);
        }

        return headersCopy;

    }

    /**
     *
     * @param {Object} headers
     * @desc bam-hls expects the headers to come in an array rather than the typical Object format
     * @returns {Array}
     *
     */
    formatRequestHeadersList(headers) {

        const errorMessage = {
            method: 'DrmProvider.formatRequestHeadersList(headers)',
            get headers() {
                return `${this.method} needs a non-empty Object`;
            }
        };

        nonEmptyObject(headers, errorMessage.headers, InvalidArgumentException);

        return Object.keys(headers).map((item) => {
            return {
                name: item,
                value: headers[item]
            };
        });

    }

    /**
     *
     * @access private
     * @desc Returns an object containing a modified copy of certificate headers if they exist
     * @returns {Object}
     *
     */
    processCertificateHeaders() {

        const headersCopy = Object.assign({}, this.certificateHeaders);
        const authHeader = headersCopy.Authorization;

        if (authHeader) {
            headersCopy.Authorization = authHeader.replace('{accessToken}', this.accessToken.token);
        }

        return headersCopy;

    }

}



// WEBPACK FOOTER //
// ./src/drm/drmProvider.js